// src/components/DealershipModal/index.js
import React, { useState, useContext, useEffect } from 'react';
import './DealershipModal.css';
import Modal from '../Modal';
import DealershipContext from '../../contexts/DealershipContext';
import UserContext from '../../contexts/UserContext';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
];

// Display helper: pretty-print 10-digit numbers, leave anything else as typed.
const formatPhoneForDisplay = (phone = '') => {
  const digits = String(phone).replace(/\D/g, '');
  return digits.length === 10
    ? `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
    : phone;
};

const DealershipModal = ({ dealership: initialDealership = null, onClose, onSubmit }) => {
  const {
    createDealership,
    updateDealership,
    changeDealershipBac,
    loading: dealershipReloading,
  } = useContext(DealershipContext);
  const { user } = useContext(UserContext);

  const isEdit = Boolean(initialDealership?.id);

  const [formData, setFormData] = useState({
    bac: null,
    name: '',
    streetAddress: '',
    addressLine2: '',
    city: '',
    state: '',
    zip: '',
    url: '',
    salesPhone: '',
    servicePhone: '',
    userId: user?.id || null,
    shippingSameAsDealership: false,
    shippingAddress1: '',
    shippingAddress2: '',
    shippingCity: '',
    shippingState: '',
    shippingPostalCode: '',
    shippingCountry: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Prefill when editing
  useEffect(() => {
    if (initialDealership) {
      setFormData({
        bac: initialDealership.bac || null,
        name: initialDealership.name || '',
        streetAddress: initialDealership.streetAddress || '',
        addressLine2: initialDealership.addressLine2 || '',
        city: initialDealership.city || '',
        state: initialDealership.state || '',
        zip: initialDealership.zip || '',
        url: initialDealership.url || '',
        salesPhone: initialDealership.salesPhone || '',
        servicePhone: initialDealership.servicePhone || '',
        shippingSameAsDealership: initialDealership.shippingSameAsDealership ?? false,
        shippingAddress1: initialDealership.shippingAddress1 || '',
        shippingAddress2: initialDealership.shippingAddress2 || '',
        shippingCity: initialDealership.shippingCity || '',
        shippingState: initialDealership.shippingState || '',
        shippingPostalCode: initialDealership.shippingPostalCode || '',
        shippingCountry: initialDealership.shippingCountry || '',
      });
    }
  }, [initialDealership, user]);

  const handleChange = e => {
    const { name, type, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]:
        name === 'bac'
          ? (value === '' ? null : Number(value))
          : name === 'salesPhone' || name === 'servicePhone'
            // store digits only; the input displays them formatted
            ? value.replace(/\D/g, '')
            : type === 'checkbox'
              ? checked
              : value
    }));
  };
  // A BAC typed in wrong can be corrected here (Lee, 2026-10-02). The server
  // moves the dealership and its orders to the new BAC and tells Martin.
  const originalBac = isEdit ? initialDealership.bac : null;
  const bacChanged = isEdit && formData.bac !== null && String(formData.bac) !== String(originalBac);

  const handleSubmitForm = async e => {
    e.preventDefault();
    setError('');
    if (isEdit && formData.bac === null) {
      setError('Enter the dealership BAC.');
      return;
    }
    // My Account does not open this form while the dealership is reloading.
    // If a reload starts with it open, saving waits: a BAC moved under a
    // running reload leaves that reload asking for the old BAC, which the
    // server refuses, and the page drops the dealership and the cart.
    if (isEdit && dealershipReloading) {
      setError('Your dealership is still loading. Wait a moment, then click Update again.');
      return;
    }
    if (bacChanged && !/^\d{6,12}$/.test(String(formData.bac))) {
      setError('A BAC is six digits. Check the number and try again.');
      return;
    }
    setLoading(true);
    try {
      const nn = v => (typeof v === 'string' ? (v.trim() === '' ? null : v.trim()) : v);
      const {
        shippingSameAsDealership,
        shippingAddress1,
        shippingAddress2,
        shippingCity,
        shippingState,
        shippingPostalCode,
        shippingCountry,
        ...rest
      } = formData;
      // [Shipping UI] BEGIN
      const shippingBlock = shippingSameAsDealership
        ? { shippingSameAsDealership: true }
        : {
          shippingSameAsDealership: false,
          shippingAddress1: nn(shippingAddress1),
          shippingAddress2: nn(shippingAddress2),
          shippingCity: nn(shippingCity),
          shippingState: nn(shippingState),
          shippingPostalCode: nn(shippingPostalCode),
          shippingCountry: nn(shippingCountry)
        };
      const payload = { ...rest, ...shippingBlock };
      // [Shipping UI] END
      let result;
      if (isEdit) {
        // The other details are saved first, under the BAC they are on now,
        // so the move to a new BAC carries them over. With a move to follow,
        // the dealership is not reloaded in between: the reload would race
        // the move and close this form before a refusal could be shown.
        result = await updateDealership({ id: initialDealership.id, ...payload }, { refetch: !bacChanged });
        if (result && result.success === false) throw new Error(result.error);
        if (bacChanged) {
          const changed = await changeDealershipBac(originalBac, formData.bac);
          if (!changed.success) throw new Error(changed.error);
          result = { ...result, bac: changed.bac };
        }
      } else {
        result = await createDealership(payload);
      }
      if (onSubmit) await onSubmit(result);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save dealership.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={isEdit ? 'Edit Dealership' : 'Create New Dealership'}
      onClose={onClose}
      maxWidth={560}
    >
      {error && <div className="error-message">{error}</div>}
      <form onSubmit={handleSubmitForm}>
        <div className="form-row">
          <label htmlFor="bac">Dealership BAC{!isEdit && '*'}</label>
          <input
            id="bac"
            name="bac"
            type="number"
            value={formData.bac ?? ''}
            onChange={handleChange}
            placeholder="Enter dealership BAC"
            required
          />
          {isEdit && !bacChanged && (
            <span className="field-hint">
              Entered it wrong? Correct it here and your orders move with it.
            </span>
          )}
          {bacChanged && (
            <span className="field-warning" role="status">
              Saving moves this dealership and its orders from BAC {String(originalBac)} to
              BAC {String(formData.bac)}. Martin Advertising will be told about the change.
            </span>
          )}
        </div>

        <div className="form-row">
          <label htmlFor="name">Dealership Name*</label>
          <input
            id="name"
            name="name"
            type="text"
            value={formData.name}
            onChange={handleChange}
            placeholder="Enter dealership name"
            required
          />
        </div>

        <div className="form-row">
          <label htmlFor="streetAddress">Street Address*</label>
          <input
            id="streetAddress"
            name="streetAddress"
            type="text"
            value={formData.streetAddress}
            onChange={handleChange}
            placeholder="Enter street address"
            required
          />
        </div>

        <div className="form-row">
          <label htmlFor="addressLine2">Address Line 2</label>
          <input
            id="addressLine2"
            name="addressLine2"
            type="text"
            value={formData.addressLine2}
            onChange={handleChange}
            placeholder="Suite, Apartment, etc."
          />
        </div>

        <div className="field-grid-3">
          <div className="form-row">
            <label htmlFor="city">City*</label>
            <input
              id="city"
              name="city"
              type="text"
              value={formData.city}
              onChange={handleChange}
              placeholder="Enter city"
              required
            />
          </div>
          <div className="form-row">
            <label htmlFor="state">State*</label>
            <select
              id="state"
              name="state"
              value={formData.state}
              onChange={handleChange}
              required
            >
              <option value="">Select</option>
              {US_STATES.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div className="form-row">
            <label htmlFor="zip">ZIP*</label>
            <input
              id="zip"
              name="zip"
              type="text"
              value={formData.zip}
              onChange={handleChange}
              placeholder="ZIP code"
              required
            />
          </div>
        </div>

        <div className="field-grid-2">
          <div className="form-row">
            <label htmlFor="salesPhone">Sales Phone*</label>
            <input
              id="salesPhone"
              name="salesPhone"
              type="tel"
              value={formatPhoneForDisplay(formData.salesPhone)}
              onChange={handleChange}
              placeholder="(555) 555-5555"
              required
            />
          </div>
          <div className="form-row">
            <label htmlFor="servicePhone">Service Phone</label>
            <input
              id="servicePhone"
              name="servicePhone"
              type="tel"
              value={formatPhoneForDisplay(formData.servicePhone)}
              onChange={handleChange}
              placeholder="(555) 555-5555"
            />
          </div>
        </div>

        <div className="form-row">
          <label htmlFor="url">Website URL*</label>
          <input
            id="url"
            name="url"
            type="url"
            value={formData.url}
            onChange={handleChange}
            placeholder="https://www.yourdealership.com"
            required
          />
        </div>

        {/* [Shipping UI] BEGIN */}
        <div className="form-subsection">
          <h3>Shipping</h3>
          <div className="checkbox-row">
            <input
              id="shippingSameAsDealership"
              type="checkbox"
              name="shippingSameAsDealership"
              checked={formData.shippingSameAsDealership}
              onChange={handleChange}
            />
            <label htmlFor="shippingSameAsDealership">
              Shipping address is the same as the dealership address
            </label>
          </div>

          {!formData.shippingSameAsDealership && (
            <>
              <div className="form-row">
                <label htmlFor="shippingAddress1">Shipping Address 1</label>
                <input id="shippingAddress1" name="shippingAddress1" type="text" value={formData.shippingAddress1} onChange={handleChange} maxLength={255} />
              </div>
              <div className="form-row">
                <label htmlFor="shippingAddress2">Shipping Address 2</label>
                <input id="shippingAddress2" name="shippingAddress2" type="text" value={formData.shippingAddress2} onChange={handleChange} maxLength={255} />
              </div>
              <div className="field-grid-2">
                <div className="form-row">
                  <label htmlFor="shippingCity">City</label>
                  <input id="shippingCity" name="shippingCity" type="text" value={formData.shippingCity} onChange={handleChange} maxLength={100} />
                </div>
                <div className="form-row">
                  <label htmlFor="shippingState">State</label>
                  <select id="shippingState" name="shippingState" value={formData.shippingState} onChange={handleChange}>
                    <option value="">Select</option>
                    {US_STATES.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div className="form-row">
                  <label htmlFor="shippingPostalCode">Postal Code</label>
                  <input id="shippingPostalCode" name="shippingPostalCode" type="text" value={formData.shippingPostalCode} onChange={handleChange} maxLength={32} pattern="[A-Za-z0-9 -]{0,32}" />
                </div>
                <div className="form-row">
                  <label htmlFor="shippingCountry">Country</label>
                  <input id="shippingCountry" name="shippingCountry" type="text" value={formData.shippingCountry} onChange={handleChange} maxLength={100} />
                </div>
              </div>
            </>
          )}
        </div>
        {/* [Shipping UI] END */}

        <div className="modal-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Saving...' : (isEdit ? 'Update' : 'Create')}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default DealershipModal;
